/**
 * zeroSwimBoot — the watchless launch-into-Club routing decision (spec v1 item;
 * D4: web-side, App.svelte owns it). Pure + injectable so it is unit-tested —
 * the exact shape of authResolved.ts.
 *
 * Auth-race safety (audit F8): on a FIRST launch the HealthKit permission sheet
 * can make listWorkouts resolve ok-with-zero for a user who HAS swims. The naive
 * "zero → Club" would strand them there. So the boot route is a REVISITABLE
 * decision until it settles: route to Club on a TRUSTED zero (loaded + offline
 * "ok" + native shell); when the `swimset:auth-resolved` refetch (or any
 * boot-window refetch) lands real swims, route BACK to Swim — but only if the
 * user is still sitting on Club (never yank someone who navigated away). A
 * swims-at-boot resolution settles immediately (never route). An error-shaped
 * resolution decides nothing — workoutsStore fills the store with STUB_WORKOUTS
 * on failure, so a non-"ok" count is not evidence either way. Settled = no
 * further action for the rest of the session.
 */
import type { OfflineReason } from "./state";
import type { TabId } from "./nav";

export interface ZeroSwimBootState {
  /** We routed to Club for a zero-swim resolution (may still be overturned). */
  routedToClub: boolean;
  /** The decision is final for this session; every later snapshot is a no-op. */
  settled: boolean;
}

export function initialZeroSwimState(): ZeroSwimBootState {
  return { routedToClub: false, settled: false };
}

export interface ZeroSwimSnapshot {
  /** workoutsLoaded — a load resolution happened (ok OR error). */
  loaded: boolean;
  /** Only "ok" is trusted: an errored load's workout array is stub placeholder data. */
  offline: OfflineReason;
  workoutCount: number;
  /** hasNativeShell() — browsers / showcase / stub mode never route. */
  nativeShell: boolean;
  /** Read NON-reactively by the caller: user navigation must never re-trigger. */
  currentTab: TabId;
}

export type ZeroSwimAction = "route-club" | "route-swim" | "none";

export function nextZeroSwimAction(
  state: ZeroSwimBootState,
  snap: ZeroSwimSnapshot,
): { action: ZeroSwimAction; state: ZeroSwimBootState } {
  if (state.settled || !snap.loaded) return { action: "none", state };

  if (!state.routedToClub) {
    if (snap.offline !== "ok") {
      // Error-shaped resolution: workouts may be STUB_WORKOUTS — no decision yet;
      // stay unsettled so a later successful refetch can still decide.
      return { action: "none", state };
    }
    if (snap.workoutCount > 0) {
      // Real swims at boot: a normal launch. Never route; done for the session.
      return { action: "none", state: { ...state, settled: true } };
    }
    if (snap.nativeShell) {
      // Final-review MINOR 3: unlike the overturn branch below, a naive first
      // route never consulted currentTab — a LATE settle (errored boot → user
      // navigates to Scores → a retry later resolves ok+zero) would yank them
      // onto Club mid-session. Gate on the viewer still sitting on Swim, same
      // untracked-read pattern as the overturn's `undo` check. Not on Swim →
      // nothing to route from; settle (never routed, so nothing to overturn
      // later either).
      if (snap.currentTab !== "swim") {
        return { action: "none", state: { ...state, settled: true } };
      }
      // Trusted zero on a real shell → open on Club. NOT settled: the
      // auth-resolved refetch may still overturn it (F8).
      return { action: "route-club", state: { ...state, routedToClub: true } };
    }
    // ok + zero in a browser: nothing to route (no native tabs to speak of) — final.
    return { action: "none", state: { ...state, settled: true } };
  }

  // Already routed to Club; a refetch landed.
  if (snap.offline === "ok" && snap.workoutCount > 0) {
    // The auth race overturned the zero. Undo — but ONLY if the user is still on
    // Club (they haven't navigated); either way the decision is now final.
    const undo = snap.currentTab === "club";
    return { action: undo ? "route-swim" : "none", state: { routedToClub: true, settled: true } };
  }
  return { action: "none", state }; // still zero — stay on Club, stay revisitable
}
